/* eslint-disable no-unused-vars */

import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import QuarterDatePicker from 'src/components/date/QuarterDatePicker';
import { useSupplier } from 'src/hooks/supplier';
import { Supplier, SupplierCompanyDetail } from 'src/types/supplier';
import {
  calculateNetAmount,
  calculateTax,
  formatCurrency,
  muiDatePickerToDateStr
} from 'src/utils';
import { TaxCertificationPDFDialog } from './TaxCertificationPDFDialog';
import { TaxCertificateData } from 'src/redux/slices/purchase-order/types';
import moment from 'moment';
import { usePermissions, useSnackBar } from 'src/hooks';
import { getQuarterEnabledMonths } from 'src/utils';
import { TaxPoSection } from './TaxPoSection';
import { useTaxCertificate } from 'src/hooks/tax-certificate/use-tax-certificate';
import { PurchaseOrderListItem } from 'src/redux/slices/purchase-order/types';
import SimpleDecisionDialog from 'src/components/dialogs/SimpleDecisionDialog';
import { useParams } from 'react-router';
import { TaxCertificatePurchaseOrder } from 'src/redux/slices/purchase-order/tax-certificate/types';

interface Props {
  isCreatePage?: boolean;
}

const TaxCertificateForm: FC<Props> = ({ isCreatePage = false }) => {
  const snackbar = useSnackBar();
  const { id } = useParams();
  const { canCreate2307Form } = usePermissions();
  const didInit = useRef(false);

  const { suppliers, isSuppliersLoading, getSuppliers } = useSupplier();
  const {
    isCreateTaxCertificateLoading,
    enablePDF,
    setEnablePDF,
    handleCreateTaxCertificate,
    fetchTaxCertificateDetails,
    isLoading,
    taxCertificateDetails,
    handleUpdateTaxCertificate
  } = useTaxCertificate();

  const [fromDate, setFromDate] = useState<MaterialUiPickersDate | null>(null);
  const [toDate, setToDate] = useState<MaterialUiPickersDate | null>(null);
  const [monthPosition, setMonthPosition] = useState<number | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );
  const [
    selectedCompanyDetail,
    setSelectedCompanyDetail
  ] = useState<SupplierCompanyDetail | null>(null);
  const [pdfMessage, setPdfMessage] = useState<string>('');
  const [openPDFDialog, setOpenPDFDialog] = useState<boolean>(false);
  const [dataIsLoaded, setDataIsLoaded] = useState<boolean>(false);
  const [pdfData, setPdfData] = useState<TaxCertificateData>({});
  const [updatedData, setUpdatedData] = useState<TaxCertificateData>({});
  const [grandTotal, setGrandTotal] = useState<number>(0);

  const [tempSupplier, setTempSupplier] = useState<Supplier | null>(null);
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<
    PurchaseOrderListItem[]
  >([]);
  const [existingPurchaseOrders, setExistingPurchaseOrders] = useState<
    TaxCertificatePurchaseOrder[]
  >([]);
  const [deletedExistingPO, setDeletedExistingPO] = useState<
    TaxCertificatePurchaseOrder[]
  >([]);
  const [
    isConfirmChangeSupplierOpen,
    setIsConfirmChangeSupplierOpen
  ] = useState<boolean>(false);

  const quarterMonthsEnabled = useMemo(
    () => (fromDate && toDate ? getQuarterEnabledMonths(fromDate, toDate) : []),
    [fromDate, toDate]
  );
  const supplierCompanyDetails = useMemo(
    () => selectedSupplier?.company_details || [],
    [selectedSupplier]
  );
  const activeCompanyDetail = useMemo<SupplierCompanyDetail | null>(() => {
    if (selectedCompanyDetail) return selectedCompanyDetail;
    if (!selectedSupplier) return null;

    return {
      name: selectedSupplier.company_name || selectedSupplier.name,
      address: selectedSupplier.address,
      zip_code: selectedSupplier.zip_code,
      terms: selectedSupplier.terms,
      tin: selectedSupplier.tin
    };
  }, [selectedCompanyDetail, selectedSupplier]);

  useEffect(() => {
    setSelectedCompanyDetail(selectedSupplier?.company_details?.[0] || null);
  }, [selectedSupplier]);

  const confirmSupplierChange = () => {
    setSelectedSupplier(tempSupplier);
    setSelectedPurchaseOrders([]); // clear old po's
    setExistingPurchaseOrders([]); // clear existing pos
    setDeletedExistingPO([]);
    setIsConfirmChangeSupplierOpen(false);
  };

  const handleGetGrandTotal = useCallback(
    (grandTotal: number) => {
      setGrandTotal(grandTotal);
    },
    [setGrandTotal]
  );

  const openTaxCertificatePDFDialog = () => {
    if (!selectedSupplier) {
      snackbar.show({
        severity: 'error',
        message: 'Please select a supplier.'
      });
      return;
    }

    if (!fromDate || !toDate) {
      snackbar.show({
        severity: 'error',
        message: 'Please select a valid period.'
      });
      return;
    }

    if (grandTotal <= 0) {
      snackbar.show({
        severity: 'error',
        message: 'Please Select at least one PO'
      });
      return;
    }

    setOpenPDFDialog(true);
  };
  const onClickCreate = useCallback(async () => {
    const res = await handleCreateTaxCertificate({
      supplier_id: selectedSupplier?.id,
      period_from: fromDate ? fromDate.toISOString() : null,
      period_to: toDate ? toDate.toISOString() : null,
      month_position_in_quarter: monthPosition,
      // @ts-ignore
      po_ids: selectedPurchaseOrders.map((po) => po?.po_id)
    });

    if (res?.success) {
      setSelectedPurchaseOrders([]); // clear old po's
      setExistingPurchaseOrders([]); // clear existing pos
      setDeletedExistingPO([]);
      setSelectedSupplier(null);
      setSelectedCompanyDetail(null);
      setTempSupplier(null);
      setFromDate(null);
      setToDate(null);
      setMonthPosition(null);
      setGrandTotal(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, handleCreateTaxCertificate, selectedSupplier, toDate]);

  const onClickUpdate = useCallback(() => {
    const mergedPoIds = [
      ...selectedPurchaseOrders.map((po) => po.po_id),
      ...existingPurchaseOrders.map((po) => po.po_id)
    ];

    if (mergedPoIds.length === 0) {
      return snackbar.show({
        severity: 'error',
        message: 'Please Select at least one PO'
      });
    }

    const period_from = muiDatePickerToDateStr(fromDate);
    const period_to = muiDatePickerToDateStr(toDate);

    handleUpdateTaxCertificate({
      tax_certificate_id: Number(id),
      supplier_id: selectedSupplier?.id,
      period_from: fromDate ? period_from : null,
      period_to: toDate ? period_to : null,
      month_position_in_quarter: monthPosition,
      // @ts-ignore
      po_ids: mergedPoIds
    });

    setPdfData(updatedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, handleUpdateTaxCertificate, selectedSupplier, toDate]);

  useEffect(() => {
    getSuppliers({
      limit: 999 // get all supplier options
    });
  }, [getSuppliers]);

  useEffect(() => {
    if (!grandTotal) return;
    setUpdatedData(() => ({
      firstMonthAmount:
        monthPosition === 1 || monthPosition === null
          ? formatCurrency(calculateTax(grandTotal), '', 2, {
              removeCurrency: true
            })
          : '-',
      secondMonthAmount:
        monthPosition === 2
          ? formatCurrency(calculateTax(grandTotal), '', 2, {
              removeCurrency: true
            })
          : '-',
      thirdMonthAmount:
        monthPosition === 3
          ? formatCurrency(calculateTax(grandTotal), '', 2, {
              removeCurrency: true
            })
          : '-',
      totalAmount: formatCurrency(calculateTax(grandTotal), '', 2, {
        removeCurrency: true
      }),
      taxWithheld: formatCurrency(calculateNetAmount(grandTotal), '', 2, {
        removeCurrency: true
      }),
      periodFrom: fromDate ? moment(fromDate).format('MM/DD/YYYY') : undefined,
      periodTo: toDate ? moment(toDate).format('MM/DD/YYYY') : undefined,
      supplierTin: activeCompanyDetail?.tin ?? undefined,
      supplierName: selectedSupplier?.name,
      supplierCompanyName: activeCompanyDetail?.name ?? undefined,
      supplierAddress: activeCompanyDetail?.address ?? undefined,
      supplierZipCode: activeCompanyDetail?.zip_code ?? undefined,
      companyTin: '010-572-019-00000',
      companyName: 'DRJ TECHNOLOGIES TRADING CORP.',
      companyAddress: '618 EARNSHAW STREET BRGY 405, ZONE 042 SAMPALOC MANILA',
      companyZipCode: '1015'
    }));

    setDataIsLoaded(true);
  }, [
    activeCompanyDetail,
    fromDate,
    toDate,
    grandTotal,
    monthPosition,
    selectedSupplier
  ]);
  // clear month position if there are no valid months selected
  useEffect(() => {
    if (
      !quarterMonthsEnabled.length ||
      !quarterMonthsEnabled.includes(monthPosition ?? 0)
    ) {
      setMonthPosition(null);
    }
  }, [
    getSuppliers,
    monthPosition,
    quarterMonthsEnabled,
    quarterMonthsEnabled.length
  ]);

  useEffect(() => {
    if (isCreatePage) return;

    const loadDetails = async () => {
      await fetchTaxCertificateDetails(Number(id));
    };
    loadDetails();

    if (!canCreate2307Form) {
      setEnablePDF(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // sets initial value on page mount
  useEffect(() => {
    if (isCreatePage) return;

    if (suppliers.length > 0 && taxCertificateDetails?.supplier_id) {
      const foundSupplier = suppliers.find(
        (supplier) => supplier.id === taxCertificateDetails.supplier_id
      );

      if (foundSupplier) {
        setSelectedSupplier(foundSupplier);
      }

      if (
        taxCertificateDetails?.period_from &&
        taxCertificateDetails?.period_to &&
        taxCertificateDetails?.month_position_in_quarter &&
        taxCertificateDetails?.purchase_orders
      ) {
        setFromDate(new Date(taxCertificateDetails?.period_from));
        setToDate(new Date(taxCertificateDetails?.period_to));

        setMonthPosition(taxCertificateDetails?.month_position_in_quarter);
        setExistingPurchaseOrders(taxCertificateDetails?.purchase_orders);
      }

      if (!dataIsLoaded) return;

      setPdfData(updatedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers, taxCertificateDetails, dataIsLoaded]);

  useEffect(() => {
    if (JSON.stringify(pdfData) === JSON.stringify(updatedData)) {
      setEnablePDF(true);
      setPdfMessage('');
    } else {
      setPdfMessage(
        'To update the contents of the Form 2307, click on the Update Certificate Button.'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData, updatedData, dataIsLoaded]);

  return (
    <Card
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1em',
        padding: '1em'
      }}
    >
      <Grid container spacing={2}>
        <Grid item container spacing={2} xs={12} sm={6}>
          <Grid item xs={12} sm={supplierCompanyDetails.length > 1 ? 6 : 12}>
            <Autocomplete
              disabled={isSuppliersLoading}
              options={suppliers}
              value={selectedSupplier}
              onChange={(_, val) => {
                if (
                  selectedPurchaseOrders.length ||
                  existingPurchaseOrders.length
                ) {
                  setIsConfirmChangeSupplierOpen(true);
                  setTempSupplier(val);
                } else {
                  setSelectedSupplier(val);
                }
              }}
              getOptionLabel={(suppliers) => suppliers?.name ?? ''}
              renderInput={(params) => (
                <TextField {...params} label="Supplier" variant="outlined" />
              )}
            />
          </Grid>
          {supplierCompanyDetails.length > 1 ? (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                disableClearable
                options={supplierCompanyDetails}
                value={selectedCompanyDetail || supplierCompanyDetails[0]}
                onChange={(_, companyDetail) => {
                  setSelectedCompanyDetail(companyDetail);
                }}
                getOptionLabel={(companyDetail) => companyDetail?.name ?? ''}
                getOptionSelected={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier Company Detail"
                    variant="outlined"
                    required
                  />
                )}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              label="TIN"
              value={activeCompanyDetail?.tin ?? ''}
              variant="outlined"
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={2} xs={12} sm={6}>
          <Grid
            item
            xs={12}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <QuarterDatePicker
              title="Period"
              fromDateValue={fromDate}
              toDateValue={toDate}
              onChangeFromDate={(date) => {
                setFromDate(date);
              }}
              onChangeToDate={(date) => {
                setToDate(date);
              }}
            />
          </Grid>
          {quarterMonthsEnabled.length ? (
            <Grid
              item
              xs={12}
              style={{
                display: 'flex',
                gap: '1em',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Typography variant="h5">Net of Vat Month</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={monthPosition}
                  onChange={(_, v) => {
                    setMonthPosition(+v);
                  }}
                  style={{ display: 'flex', flexDirection: 'row' }}
                >
                  {quarterMonthsEnabled.includes(1) ? (
                    <FormControlLabel
                      value={1}
                      control={<Radio />}
                      label="1st Month"
                    />
                  ) : null}
                  {quarterMonthsEnabled.includes(2) ? (
                    <FormControlLabel
                      value={2}
                      control={<Radio />}
                      label="2nd Month"
                    />
                  ) : null}
                  {quarterMonthsEnabled.includes(3) ? (
                    <FormControlLabel
                      value={3}
                      control={<Radio />}
                      label="3rd Month"
                    />
                  ) : null}
                </RadioGroup>
              </FormControl>
            </Grid>
          ) : null}
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            label="Company Name"
            value={activeCompanyDetail?.name ?? ''}
            variant="outlined"
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Address"
            value={activeCompanyDetail?.address ?? ''}
            variant="outlined"
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Grid>
      </Grid>

      <Divider style={{ margin: '1em 0' }} />

      <TaxPoSection
        supplier={selectedSupplier}
        selectedPurchaseOrders={selectedPurchaseOrders}
        setSelectedPurchaseOrders={(payload: PurchaseOrderListItem[]) => {
          setSelectedPurchaseOrders(payload);
        }}
        getGrandTotal={handleGetGrandTotal}
        isCreatePage={isCreatePage}
        existingPurchaseOrders={existingPurchaseOrders}
        modifyExistingPurchaseOrders={setExistingPurchaseOrders}
        deletedExistingPO={deletedExistingPO}
        setDeletedExistingPO={setDeletedExistingPO}
      />

      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <Box
          display="flex"
          flexDirection="column"
          style={{ gap: 10, maxWidth: 180 }}
        >
          <Box display="flex" flexDirection="column">
            {!isCreatePage ? (
              <Tooltip title={pdfMessage}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => openTaxCertificatePDFDialog()}
                  disabled={!enablePDF}
                >
                  Print 2307
                </Button>
              </Tooltip>
            ) : null}
          </Box>

          {isCreatePage ? (
            <Button
              disabled={isCreateTaxCertificateLoading}
              variant="contained"
              color="primary"
              onClick={onClickCreate}
            >
              Create Certificate
            </Button>
          ) : (
            <Button
              disabled={isCreateTaxCertificateLoading || !canCreate2307Form}
              variant="contained"
              color="primary"
              onClick={onClickUpdate}
            >
              Update Certificate
            </Button>
          )}
        </Box>
      </div>

      <TaxCertificationPDFDialog
        pdfData={pdfData}
        open={openPDFDialog}
        handleClose={() => setOpenPDFDialog(false)}
      />

      <SimpleDecisionDialog
        isOpen={isConfirmChangeSupplierOpen}
        title="Change supplier"
        subTitle="Changing the supplier will clear the current purchase order list. Are you sure you want to continue?"
        disableBackDropClick={true}
        onHandleConfirmAction={confirmSupplierChange} // how do we do this
        onHandleClose={() => setIsConfirmChangeSupplierOpen(false)}
      />
    </Card>
  );
};

export default memo(TaxCertificateForm);
